import React, { useRef, useState } from 'react';
import {
	Box,
	IconButton,
	Popover,
	Tooltip,
	CircularProgress,
	Typography,
	SvgIcon,
	Grid
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import AppsIcon from '@mui/icons-material/Apps';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { TRANSLATE } from 'src/submodules/commons-misc/constant';
import configService from 'src/services/configService';
import userInfo from 'src/services/userInfo';
import { authService } from 'src/services';
import layoutService from 'src/services/layoutService';

const useStyles = makeStyles(theme => ({
	popoverPaper: {
		padding: theme.spacing(2),
		borderRadius: 12,
		boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
		maxWidth: 640,
		width: '90vw',
		[theme.breakpoints.down('xs')]: {
			padding: theme.spacing(1.5),
			maxWidth: '95vw'
		}
	},
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
		gap: theme.spacing(1.5),
		maxWidth: 640,
		width: '100%',
		[theme.breakpoints.down('sm')]: {
			gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
			gap: theme.spacing(1)
		}
	},
	appCard: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		height: 120,
		background: '#fff',
		borderRadius: 12,
		boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
		transition:
			'transform .18s ease, box-shadow .18s ease, background .18s ease',
		cursor: 'pointer',
		padding: theme.spacing(1.5),
		'&:hover': {
			transform: 'translateY(-3px)',
			boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
			background: '#f9f9f9'
		},
		[theme.breakpoints.down('sm')]: {
			height: 100,
			padding: theme.spacing(1),
			borderRadius: 10,
			boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
		}
	},
	appCardDisabled: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		height: 120,
		backgroundColor: '#dbdbdb',
		color: '#a9a9a9',
		borderRadius: 12,
		boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
		padding: theme.spacing(1.5),
		[theme.breakpoints.down('sm')]: {
			height: 100,
			padding: theme.spacing(1),
			borderRadius: 10
		}
	},
	label: {
		marginTop: 6,
		fontWeight: 500,
		maxWidth: 100,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		textAlign: 'center',
		fontSize: 14,
		[theme.breakpoints.down('sm')]: {
			fontSize: 12
		}
	},
	removeBtn: {
		position: 'absolute',
		top: 4,
		right: 4,
		width: 24,
		height: 24,
		background: 'rgba(255,255,255,0.9)',
		borderRadius: 12,
		'&:hover': {
			background: 'rgba(255,0,0,0.12)',
			color: '#d32f2f'
		}
	},
	addCard: {
		height: 120,
		borderRadius: 12,
		background: '#f5f5f5',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		transition:
			'transform .18s ease, background .18s ease, box-shadow .18s ease',
		cursor: 'pointer',
		'&:hover': {
			transform: 'translateY(-3px)',
			background: '#efefef',
			boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
		}
	}
}));

const sanitizeUrl = u => {
	let s = (u || '').trim();
	if (!/^https?:\/\//i.test(s)) s = 'https://' + s;
	return s;
};

export default function AppsMenu({ webApps }) {
	const classes = useStyles();
	const isMobile = layoutService.isMobile();
	const ref = useRef(null);
	const [isOpen, setIsOpen] = useState(false);
	const wa = webApps || configService.getWebApps();
	const rows = configService.getWebAppRows();
	const [defaultApps] = useState(wa || []);
	const [isLoading] = useState(!Boolean(wa));
	const { t } = useTranslation();

	const goTo = (url, newTab = false) => {
		if (window.location.href.indexOf(url) !== 0) {
			// const token = authService.getAccessToken();
			if (newTab) {
				const safe = sanitizeUrl(url);
				window.open(safe, '_blank', 'noopener,noreferrer');
			} else {
				localStorage.removeItem('G7');
				window.location.href = url;
				// window.location.href = url + `?token=${token}`;
			}
		}
	};
	const getRow = row => {
		const appEnabled = defaultApps?.filter(a => userInfo.can(a?.auth));
		const numAppEnabled = appEnabled?.length || null;
		if (row === undefined || row === null || row === '') {
			if (numAppEnabled !== null && Number(numAppEnabled) < 3) {
				return 12 / Number(numAppEnabled);
			}
			return 12 / 3;
		} else {
			if (numAppEnabled !== null && Number(numAppEnabled) < Number(row)) {
				return 12 / Number(numAppEnabled);
			}
			return 12 / Number(row);
		}
	};

	const renderAppCard = (w, idx, isCustom = false) => (
		<Grid item xs={isMobile ? 6 : getRow(rows)} lg={getRow(rows)}>
			<Box
				key={idx}
				className={
					window.location.href.indexOf(w?.url) !== 0
						? classes.appCard
						: classes.appCardDisabled
				}
				onClick={() => goTo(w?.url, isCustom)}
				role="button"
				aria-label={w?.label}
			>
				<Icon
					icon={w?.icon || 'tabler:home-move'}
					width={isMobile ? '36' : '42'}
					height={isMobile ? '36' : '42'}
				/>
				<Typography variant="body2" className={classes.label}>
					{w?.label}
				</Typography>
			</Box>
		</Grid>
	);

	return (
		<>
			<Tooltip title={t(TRANSLATE.APPLICATIONS)}>
				{isLoading ? (
					<CircularProgress color="inherit" size={20} />
				) : (
					<IconButton
						color="inherit"
						onClick={() => setIsOpen(true)}
						ref={ref}
						size={isMobile ? 'medium' : 'large'}
					>
						<SvgIcon fontSize={isMobile ? 'medium' : 'small'}>
							<AppsIcon />
						</SvgIcon>
					</IconButton>
				)}
			</Tooltip>

			{!isLoading && (
				<Popover
					anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
					transformOrigin={{ vertical: 'top', horizontal: 'center' }}
					anchorEl={ref.current}
					open={isOpen}
					onClose={() => setIsOpen(false)}
					style={{
						maxHeight: isMobile ? '70vh' : 'auto',
						overflowY: 'auto'
					}}
					PaperProps={{
						className: classes.popoverPaper
					}}
				>
					<Grid container spacing={isMobile ? 1 : 2}>
						{defaultApps.map((w, idx) =>
							userInfo.can(w?.auth) ? renderAppCard(w, idx, w?.external) : null
						)}
					</Grid>
				</Popover>
			)}
		</>
	);
}
